import { Box, FormControl, Input, useToast, VStack } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactSelectOption, ServerError } from '../../../../types';
import { request } from '../../../../utils/helpers/httpHelpers';
import {
  convertToEnum,
  getEnumNamesAndValues,
} from '../../../../utils/helpers/enumHelpers';
import { addSpaceBeforeUppercaseLetter } from '../../../../utils/helpers/commonHelpers';
import {
  UserProfile,
  CreateUserProfileRequest,
  userProfilesApi,
  UserProfileType,
} from '@texas/api/endpoints/userProfilesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';

export interface CreateUserProfileFormProps {
  userId: number;
  onProfileCreated: (userProfile: UserProfile) => void;
}

const defaultValues = {
  name: '',
  type: UserProfileType.KeyAccountManager,
};

export function CreateUserProfileForm(props: CreateUserProfileFormProps) {
  const toast = useToast();
  const { t } = useTranslation();
  const { request: createUserProfileRequest, error } = useApiRequest(
    userProfilesApi.createUserProfile,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    control,
  } = useForm<CreateUserProfileRequest>({
    defaultValues: {
      userId: props.userId,
      type: UserProfileType.KeyAccountManager,
    },
  });

  const onSubmit = async (data: CreateUserProfileRequest) =>
    await request(
      createUserProfileRequest,
      [data],
      (data: UserProfile) => {
        reset({ ...defaultValues, userId: props.userId });
        toast({
          title: t('general.created'),
          status: 'success',
          isClosable: true,
        });
        props.onProfileCreated(data);
      },
      (error: ServerError) => {
        toast({
          title: t('general.createFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  return (
    <Box>
      <VStack>
        {error && <ErrorDetails error={error} />}
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          <VStack>
            <FormControl isInvalid={!!errors.type}>
              <TexasFormLabel>
                {t('userProfile.userProfileType')}
              </TexasFormLabel>
              <Controller
                name="type"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('userProfile.profileTypeRequired'),
                  },
                }}
                render={({ field }) => (
                  <TexasSelect
                    {...field}
                    isClearable={false}
                    value={GetProfileTypeOption()}
                    onChange={(e) => {
                      field.onChange(convertToEnum(UserProfileType, e?.value));
                    }}
                    options={getEnumNamesAndValues(
                      UserProfileType,
                    ).map<ReactSelectOption>((p) => ({
                      label: addSpaceBeforeUppercaseLetter(p.name),
                      value: p.value,
                    }))}
                    closeMenuOnSelect={true}
                  />
                )}
              />
              <ErrorLabel text={errors.type?.message} />
            </FormControl>
            <FormControl isRequired={true} isInvalid={!!errors.name}>
              <TexasFormLabel>
                {t('userProfile.userProfileName')}
              </TexasFormLabel>
              <Input
                variant="outline"
                {...register('name', {
                  required: true,
                  maxLength: {
                    value: 25,
                    message: t('errors.maxLength', { count: 25 }),
                  },
                })}
                placeholder="Name"
              />
              <ErrorLabel text={errors.name?.message} />
            </FormControl>
            <SubmitButton loading={isSubmitting}>
              {t('general.create')}
            </SubmitButton>
          </VStack>
        </form>
      </VStack>
    </Box>
  );
}

function GetProfileTypeOption(): ReactSelectOption {
  return { label: 'Key Account Manager', value: 1 };
}
